import { Inject, Injectable } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DayOfWeek, HostService, TimeOfDay, TimeRange as TimeSlot, WeekdayAvailability } from '@vendasta/meetings';
import moment from 'moment-timezone';
import { Observable, from, of } from 'rxjs';
import { catchError, filter, first, switchMap } from 'rxjs/operators';
import { MeetingSchedulerServiceModule } from '../data-providers/meeting-scheduler-service.module';
import { MeetingSchedulerStoreService } from '../data-providers/meeting-scheduler-store.service';
import { HOST_ID_TOKEN, INITIAL_AVAILABILITY_HOURS } from '../data-providers/providers';
import { DailyAvailability, TimeRange } from '../interface';
import { availabilityRulesToDailyAvailability, loadTimeOptions } from '../utils';

export const indexToDayOfWeekMapping = [
  { id: DayOfWeek.MONDAY, name: 'Mon' },
  { id: DayOfWeek.TUESDAY, name: 'Tues' },
  { id: DayOfWeek.WEDNESDAY, name: 'Weds' },
  { id: DayOfWeek.THURSDAY, name: 'Thurs' },
  { id: DayOfWeek.FRIDAY, name: 'Fri' },
  { id: DayOfWeek.SATURDAY, name: 'Sat' },
  { id: DayOfWeek.SUNDAY, name: 'Sun' },
];

@Injectable({
  providedIn: MeetingSchedulerServiceModule,
})
export class ScheduleSettingsService {
  readonly timeOptions: Date[];

  constructor(
    private readonly hostService: HostService,
    private readonly alertService: SnackbarService,
    private readonly meetingSchedulerService: MeetingSchedulerStoreService,
    @Inject(HOST_ID_TOKEN) readonly hostId$: Observable<string>,
    @Inject(INITIAL_AVAILABILITY_HOURS) private readonly initialAvailabilityHours$: Observable<DailyAvailability>,
  ) {
    this.timeOptions = loadTimeOptions();
  }

  private dateToTimeOfDay(date: Date): TimeOfDay {
    return {
      hours: date.getHours(),
      minutes: date.getMinutes(),
      seconds: date.getSeconds(),
      nanos: date.getMilliseconds() * 1000000,
    } as TimeOfDay;
  }

  private timeRangeToTimeSlot(range: TimeRange): TimeSlot {
    return { from: this.dateToTimeOfDay(range.start), to: this.dateToTimeOfDay(range.end) };
  }

  // This exists because the view layer has a reference issue.
  // It compares dates by reference to determine which time is selected.
  // When you compare two dates that represent the same point in time they are not equal
  // Because their references are different.
  getEquivalentTimeOption(d: Date): Date {
    const dTime = d.getTime();
    return this.timeOptions.find((d2) => d2.getTime() === dTime);
  }

  setAvailability(
    availability: DailyAvailability,
    timezone?: string,
    meetingId?: string,
    hostId?: string,
  ): Observable<void> {
    const weekdaysAvailability: WeekdayAvailability[] = [];
    availability.days.forEach((ranges, day) => {
      weekdaysAvailability.push(
        new WeekdayAvailability({
          day,
          timeSlots: ranges.map((range) => this.timeRangeToTimeSlot(range)),
        }),
      );
    });

    const hostIdSource = hostId ? of(hostId) : this.hostId$.pipe(filter<string>(Boolean), first());

    return hostIdSource.pipe(
      filter<string>(Boolean),
      first(),
      switchMap((hostId) => {
        const request = {
          hostId,
          meetingTypeId: meetingId,
          weekdaysAvailability: weekdaysAvailability,
          timeZone: { id: timezone || moment.tz.guess() },
        };
        return this.hostService.setGeneralAvailability(request);
      }),
    );
  }

  async loadInitialAvailability(meetingTypeId?: string): Promise<DailyAvailability> {
    const hostId = await this.hostId$.pipe(filter<string>(Boolean), first()).toPromise();
    const defaultAvailabilityPromise = this.getDefaultAvailability();
    const responsePromise = this.hostService
      .listAvailability({
        filters: {
          hostId,
          meetingTypeId,
        },
        pagingOptions: {
          pageSize: 100,
        },
        timeZone: { id: moment.tz.guess() },
      })
      .pipe(
        first(),
        catchError(() => {
          this.alertService.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_LOAD_ERROR');
          return from(defaultAvailabilityPromise.then(() => ({ results: [] })));
        }),
      )
      .toPromise();

    const [response, defaultAvailability, hasHostEverBeenConfigured] = await Promise.all([
      responsePromise,
      defaultAvailabilityPromise,
      this.meetingSchedulerService.loadHasHostEverBeenConfigured().pipe(first()).toPromise(),
    ]);

    const rules = response.results || [];
    if (rules.length === 0 && !hasHostEverBeenConfigured) {
      return defaultAvailability;
    }

    return availabilityRulesToDailyAvailability(rules, this.timeOptions);
  }

  getDefaultAvailability(): Promise<DailyAvailability> {
    return this.initialAvailabilityHours$.pipe(first()).toPromise();
  }
}
